import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { AlertTriangle, CheckCircle, Download, GripVertical, Pencil, PenLine, Trophy } from "lucide-react";
import type { AdminDriver, AdminRace } from "./types";

type ResultRow = {
  driver_number: number;
  laps_completed: number | null;
  dnf: boolean;
  dns: boolean;
  dsq: boolean;
  gap_to_leader: number | null;
  q1_time: number | null;
  q2_time: number | null;
  q3_time: number | null;
  best_quali_time: number | null;
  auto_assigned?: boolean;
};

type FetchedRow = ResultRow & { position: number | null; suggested_position: number | null };

type FetchedResults = {
  round: number;
  race_id: string;
  is_sprint: boolean;
  qualifying: FetchedRow[];
  sprint: FetchedRow[] | null;
  race: FetchedRow[];
};

type ScoreSummary = {
  round: number;
  is_sprint: boolean;
  pole_driver: number;
  pole_margin_ms: number;
  matchups_resolved: number;
  matchup_picks_scored: number;
  race_picks_scored: number;
};

type SessionKey = "qualifying" | "sprint" | "race";
type SourceMode = "fetched" | "manual" | "saved";

// Ordered arrays are the single source of truth: position = index + 1.
type SessionData = Record<SessionKey, ResultRow[] | null>;

const SESSION_LABELS: Record<SessionKey, string> = {
  qualifying: "Qualifying",
  sprint: "Sprint",
  race: "Race",
};

const SESSION_NAME_TO_KEY: Record<string, SessionKey> = {
  Qualifying: "qualifying",
  Sprint: "sprint",
  Race: "race",
};

// Base q3 time used to synthesize P1/P2 times from a manually entered margin.
const SYNTH_P1_Q3 = 90.0;

function orderFetched(rows: FetchedRow[]): ResultRow[] {
  return [...rows]
    .sort((a, b) => (a.position ?? a.suggested_position ?? 99) - (b.position ?? b.suggested_position ?? 99))
    .map(({ position, suggested_position, ...rest }) => ({
      ...rest,
      auto_assigned: position === null,
    }));
}

function emptyRow(driverNumber: number): ResultRow {
  return {
    driver_number: driverNumber,
    laps_completed: null,
    dnf: false,
    dns: false,
    dsq: false,
    gap_to_leader: null,
    q1_time: null,
    q2_time: null,
    q3_time: null,
    best_quali_time: null,
  };
}

function marginFromRows(rows: ResultRow[]): string {
  const p1 = rows[0]?.q3_time;
  const p2 = rows[1]?.q3_time;
  if (typeof p1 !== "number" || typeof p2 !== "number") return "";
  return String(Math.round((p2 - p1) * 1000));
}

const SortableRow = ({
  row,
  index,
  driver,
  onJump,
  onToggleFlag,
}: {
  row: ResultRow;
  index: number;
  driver: AdminDriver | undefined;
  onJump: (newPosition: number) => void;
  onToggleFlag: (flag: "dnf" | "dns" | "dsq") => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.driver_number,
  });
  // Local text state so typing "12" doesn't jump the row at "1".
  const [posDraft, setPosDraft] = useState<string | null>(null);

  const commitJump = () => {
    if (posDraft !== null) {
      const parsed = Number(posDraft);
      if (Number.isInteger(parsed) && parsed >= 1) onJump(parsed);
      setPosDraft(null);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-md px-1 py-1 ${isDragging ? "z-10 bg-accent shadow-lg" : row.auto_assigned ? "bg-yellow-100/60 dark:bg-yellow-900/20" : ""}`}
    >
      <button
        className="touch-none cursor-grab p-1.5 text-muted-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${driver?.name_acronym ?? row.driver_number}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Input
        type="number"
        inputMode="numeric"
        className="h-8 w-12 px-1 text-center text-sm"
        value={posDraft ?? String(index + 1)}
        onFocus={(e) => {
          setPosDraft(String(index + 1));
          e.target.select();
        }}
        onChange={(e) => setPosDraft(e.target.value)}
        onBlur={commitJump}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <span
        className="h-4 w-1 shrink-0 rounded-full"
        style={{ backgroundColor: driver?.team_colour ? `#${driver.team_colour}` : "#666" }}
      />
      <span className="flex-1 min-w-0 truncate text-sm font-medium">
        {driver?.name_acronym ?? `#${row.driver_number}`}
        <span className="ml-1 text-xs text-muted-foreground">{driver?.full_name}</span>
      </span>
      {row.laps_completed !== null && (
        <span className="text-[11px] text-muted-foreground whitespace-nowrap">{row.laps_completed} laps</span>
      )}
      {(["dnf", "dns", "dsq"] as const).map((flag) => (
        <button
          key={flag}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${row[flag] ? "bg-red-500 text-white" : "bg-muted text-muted-foreground"}`}
          onClick={() => onToggleFlag(flag)}
        >
          {flag}
        </button>
      ))}
    </div>
  );
};

const ResultsTab = ({
  race,
  driverMap,
  drivers,
}: {
  race: AdminRace | undefined;
  driverMap: Record<number, AdminDriver>;
  drivers: AdminDriver[];
}) => {
  const queryClient = useQueryClient();
  const [sessions, setSessions] = useState<SessionData | null>(null);
  const [sourceMode, setSourceMode] = useState<SourceMode>("fetched");
  const [poleMargin, setPoleMargin] = useState("");
  const [marginEdited, setMarginEdited] = useState(false);
  const [loading, setLoading] = useState<"fetch" | "saved" | null>(null);
  const [committing, setCommitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [summary, setSummary] = useState<ScoreSummary | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
  );

  const resetWith = (data: SessionData, mode: SourceMode, margin: string) => {
    setSessions(data);
    setSourceMode(mode);
    setPoleMargin(margin);
    setMarginEdited(false);
    setSummary(null);
  };

  const handleFetch = async () => {
    if (!race) return;
    setLoading("fetch");
    try {
      const data = await invokeEdgeFunction<FetchedResults>("admin-fetch-results", {
        round: race.round_number,
      });
      const qualifying = orderFetched(data.qualifying);
      resetWith(
        {
          qualifying,
          sprint: data.sprint ? orderFetched(data.sprint) : null,
          race: orderFetched(data.race),
        },
        "fetched",
        marginFromRows(qualifying),
      );
    } catch (e) {
      toast({ title: "Fetch failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  const handleManual = () => {
    if (!race) return;
    const teamOrdered = [...drivers].sort(
      (a, b) => a.team_name.localeCompare(b.team_name) || a.driver_number - b.driver_number,
    );
    const seed = () => teamOrdered.map((d) => emptyRow(d.driver_number));
    resetWith(
      {
        qualifying: seed(),
        sprint: race.is_sprint_weekend ? seed() : null,
        race: seed(),
      },
      "manual",
      "",
    );
  };

  const handleLoadSaved = async () => {
    if (!race) return;
    setLoading("saved");
    try {
      const { data, error } = await supabase
        .from("race_results")
        .select("*, sessions!inner(race_id, session_name)")
        .eq("sessions.race_id", race.id)
        .in("sessions.session_name", ["Qualifying", "Sprint", "Race"]);
      if (error) throw error;
      if (!data || data.length === 0) {
        toast({ title: "No saved results", description: "Nothing in race_results for this round yet." });
        return;
      }
      const pending: Record<SessionKey, { pos: number; row: ResultRow }[]> = {
        qualifying: [],
        sprint: [],
        race: [],
      };
      for (const r of data) {
        const key = SESSION_NAME_TO_KEY[(r.sessions as { session_name: string }).session_name];
        if (!key) continue;
        pending[key].push({
          pos: r.position ?? 99,
          row: {
            driver_number: r.driver_number,
            laps_completed: r.laps_completed,
            dnf: r.dnf ?? false,
            dns: r.dns ?? false,
            dsq: r.dsq ?? false,
            gap_to_leader: r.gap_to_leader,
            q1_time: r.q1_time,
            q2_time: r.q2_time,
            q3_time: r.q3_time,
            best_quali_time: r.best_quali_time,
            auto_assigned: false,
          },
        });
      }
      const grouped: SessionData = { qualifying: null, sprint: null, race: null };
      for (const key of Object.keys(pending) as SessionKey[]) {
        if (pending[key].length) {
          grouped[key] = pending[key].sort((a, b) => a.pos - b.pos).map((e) => e.row);
        }
      }
      resetWith(grouped, "saved", grouped.qualifying ? marginFromRows(grouped.qualifying) : "");
    } catch (e) {
      toast({ title: "Load failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  // Row edits that do not change order (DNF/DNS/DSQ flags) — pure, no margin impact.
  const updateSession = (key: SessionKey, updater: (rows: ResultRow[]) => ResultRow[]) => {
    setSessions((prev) => {
      if (!prev || !prev[key]) return prev;
      return { ...prev, [key]: updater(prev[key]!) };
    });
  };

  // Applies a reorder and keeps the pole margin honest. Reordering qualifying changes
  // who is P1/P2, so a margin derived from the old order is no longer correct — without
  // this, a grid-penalty or DSQ reorder left the displayed margin stale while score_race
  // computed the gap between the *new* top two, which can be negative.
  // Kept out of the setSessions updater so the updater stays pure (StrictMode
  // double-invokes updaters; setState calls inside one would fire twice).
  const reorderSession = (key: SessionKey, updater: (rows: ResultRow[]) => ResultRow[]) => {
    const current = sessions?.[key];
    if (!current) return;
    const next = updater(current);
    setSessions((prev) => (prev ? { ...prev, [key]: next } : prev));
    if (key === "qualifying") {
      const recomputed = marginFromRows(next);
      setPoleMargin(recomputed);
      // Blank means we cannot derive it (missing q3 times) — the admin must type it,
      // and marginValid blocks the commit until they do. A negative value is likewise
      // rejected by marginValid, which is what we want after an out-of-order promotion.
      setMarginEdited(recomputed === "");
    }
  };

  const handleDragEnd = (key: SessionKey) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderSession(key, (rows) => {
      const from = rows.findIndex((r) => r.driver_number === active.id);
      const to = rows.findIndex((r) => r.driver_number === over.id);
      return arrayMove(rows, from, to).map((r) => ({ ...r, auto_assigned: false }));
    });
  };

  const handleJump = (key: SessionKey, index: number, newPosition: number) => {
    reorderSession(key, (rows) => {
      const to = Math.min(Math.max(newPosition - 1, 0), rows.length - 1);
      if (to === index) return rows;
      return arrayMove(rows, index, to).map((r) => ({ ...r, auto_assigned: false }));
    });
  };

  // The margin must be valid whatever the source. Fetched data can still yield an
  // empty margin (marginFromRows returns "" when P1 or P2 has no q3_time, which is
  // what OpenF1 gives us when the duration array is short), and a reorder of the
  // qualifying grid can leave the displayed margin stale or negative. Short-circuiting
  // to true here let all of those through and wrote a NULL actual_pole_margin for the
  // whole round, silently killing the tiebreaker.
  const marginValid = useMemo(() => {
    const n = Number(poleMargin);
    return poleMargin !== "" && Number.isInteger(n) && n >= 0;
  }, [poleMargin]);

  const autoAssignedCount = sessions
    ? [...(sessions.qualifying ?? []), ...(sessions.sprint ?? []), ...(sessions.race ?? [])].filter((r) => r.auto_assigned).length
    : 0;

  const handleCommit = async () => {
    if (!race || !sessions) return;
    setCommitting(true);
    try {
      const toPayload = (rows: ResultRow[], isQuali: boolean) =>
        rows.map(({ auto_assigned, ...rest }, i) => {
          const row = { ...rest, position: i + 1 };
          // Synthesize P1/P2 q3 times when the margin was entered/overridden,
          // so score_race computes exactly the entered margin.
          if (isQuali && (sourceMode === "manual" || marginEdited)) {
            const margin = Number(poleMargin);
            if (i === 0) row.q3_time = SYNTH_P1_Q3;
            if (i === 1) row.q3_time = SYNTH_P1_Q3 + margin / 1000;
          }
          return row;
        });

      const data = await invokeEdgeFunction<{ summary: ScoreSummary }>("admin-commit-results", {
        round: race.round_number,
        qualifying: toPayload(sessions.qualifying!, true),
        sprint: sessions.sprint ? toPayload(sessions.sprint, false) : null,
        race: toPayload(sessions.race!, false),
      });
      setSummary(data.summary);
      toast({ title: `Round ${race.round_number} scored` });
      queryClient.invalidateQueries({ queryKey: ["admin-races"] });
      queryClient.invalidateQueries({ queryKey: ["admin-matchups"] });
      queryClient.invalidateQueries({ queryKey: ["admin-quality-stats"] });
    } catch (e) {
      toast({ title: "Scoring failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setCommitting(false);
      setConfirmOpen(false);
    }
  };

  const renderSession = (key: SessionKey) => {
    const rows = sessions?.[key];
    if (!rows) return null;
    return (
      <Card key={key}>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{SESSION_LABELS[key]}</h3>
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-[10px]">
              {rows.length} drivers
            </Badge>
          </div>
          {key === "qualifying" && (
            <div className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5">
              <span className="text-xs font-medium text-muted-foreground">Pole margin (ms)</span>
              <Input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 237"
                className={`h-8 w-24 text-sm ${!marginValid ? "border-destructive" : ""}`}
                value={poleMargin}
                onChange={(e) => {
                  setPoleMargin(e.target.value);
                  setMarginEdited(true);
                }}
              />
              <span className="text-[11px] text-muted-foreground">P2 minus P1 in qualifying</span>
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(key)}>
            <SortableContext items={rows.map((r) => r.driver_number)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {rows.map((r, i) => (
                  <SortableRow
                    key={r.driver_number}
                    row={r}
                    index={i}
                    driver={driverMap[r.driver_number]}
                    onJump={(pos) => handleJump(key, i, pos)}
                    onToggleFlag={(flag) =>
                      updateSession(key, (rs) =>
                        rs.map((row) => (row.driver_number === r.driver_number ? { ...row, [flag]: !row[flag] } : row)),
                      )
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>
    );
  };

  if (!race) return <p className="text-sm text-muted-foreground">Select a round first.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={handleFetch} disabled={loading !== null}>
          <Download className="mr-1.5 h-4 w-4" />
          {loading === "fetch" ? "Fetching…" : "Fetch from OpenF1"}
        </Button>
        <Button variant="outline" onClick={handleManual} disabled={loading !== null}>
          <PenLine className="mr-1.5 h-4 w-4" /> Manual entry
        </Button>
        {race.status === "completed" && (
          <Button variant="outline" onClick={handleLoadSaved} disabled={loading !== null}>
            <Pencil className="mr-1.5 h-4 w-4" />
            {loading === "saved" ? "Loading…" : "Edit saved results"}
          </Button>
        )}
      </div>

      {sessions && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">
            {sourceMode === "fetched" ? "Source: OpenF1" : sourceMode === "manual" ? "Source: manual entry" : "Source: saved results"}
          </Badge>
          {race.status === "completed" && (
            <span className="text-[11px] text-muted-foreground">re-scoring is safe — picks are recalculated</span>
          )}
        </div>
      )}

      {summary && (
        <Card className="border-green-500/50">
          <CardContent className="p-4 space-y-1">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Trophy className="h-4 w-4 text-green-500" /> Round {summary.round} scored
            </div>
            <p className="text-sm text-muted-foreground">
              Pole: {driverMap[summary.pole_driver]?.name_acronym ?? `#${summary.pole_driver}`} by {summary.pole_margin_ms}ms
              · {summary.matchups_resolved} matchups resolved
              · {summary.matchup_picks_scored} matchup picks scored
              · {summary.race_picks_scored} race picks scored
            </p>
          </CardContent>
        </Card>
      )}

      {sessions && (
        <>
          {autoAssignedCount > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-yellow-100 p-2 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {autoAssignedCount} DNF/DNS position{autoAssignedCount === 1 ? "" : "s"} auto-assigned (highlighted) — drag to correct if needed
            </div>
          )}
          {!marginValid && (
            <div className="flex items-center gap-2 rounded-md bg-red-100 p-2 text-xs text-red-800 dark:bg-red-900/30 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Enter the pole margin (whole milliseconds) before scoring
            </div>
          )}

          {renderSession("qualifying")}
          {renderSession("sprint")}
          {renderSession("race")}

          <Button className="w-full font-semibold" disabled={!marginValid || committing} onClick={() => setConfirmOpen(true)}>
            <CheckCircle className="mr-1.5 h-4 w-4" />
            {committing ? "Scoring…" : "Save & Score"}
          </Button>
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Score Round {race.round_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save the results, resolve all matchups, and score every user's picks for Round {race.round_number} — {race.meeting_name}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={committing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCommit} disabled={committing}>
              {committing ? "Scoring…" : "Save & Score"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ResultsTab;
